// Pure derivation behind components/pseudonymous-avatar.tsx, split into
// its own dependency-free module (rather than living in that file
// directly, which is this codebase's usual "pure derivation, tested
// separately" convention -- see use-tier0-state.ts's deriveTier0Phase)
// for a jest-specific reason, not a style one: pseudonymous-avatar.tsx
// imports 'tamagui' for its View/Text, and jest can't parse tamagui's
// native ESM entry (`Cannot use import statement outside a module`,
// confirmed by actually running the test suite with the logic still
// inlined in that file) -- no jest transform for it is configured for
// this project, and none of this codebase's other tests import
// 'tamagui' either, so this file exists specifically to keep the
// tested logic reachable without pulling that import in.

// FNV-1a (32-bit). Not cryptographic -- this only needs to be stable
// (same string in, same bucket out, every time, on every platform) and
// reasonably well-distributed across a handful of buckets, which FNV-1a
// gives in a few lines with no dependency. `>>> 0` folds the signed
// 32-bit result Math.imul produces back into an unsigned range before
// the modulo, so the bucket index is never negative.
function hashToIndex(seed: string, bucketCount: number): number {
  let hash = 0x811c9dc5;
  for (let i = 0; i < seed.length; i++) {
    hash ^= seed.charCodeAt(i);
    hash = Math.imul(hash, 0x01000193);
  }
  return (hash >>> 0) % bucketCount;
}

// Five bg/text pairs, chosen deliberately over inventing new hex: each
// is an EXISTING token from src/theme/tokens.generated.ts (generated
// from tokens/colors.json), and together they're exactly this token
// system's five non-neutral primitive hue families (stamp, pine, amber,
// red) each already paired at the same bg-100/text-700 shade step used
// for contrast -- the same "reference primitives directly for a small
// decorative/categorical rotation" pattern tokens/data-viz.json
// documents for chart series colors. An avatar-identity palette is the
// same kind of use: pure decoration that distinguishes one person from
// another, not a status signal. Reusing "warning"/"error" hues here is
// safe for exactly that reason -- nothing about this circle conveys
// app state, so a handle that happens to hash into the amber or red
// slot doesn't read as "this post has a problem" any more than a
// randomly-tinted GitHub avatar does. Measured text/background
// contrast, all clearing the 4.5:1 AA floor: neutral 9.07:1, primary/
// stamp 8.77:1, success/pine 8.85:1, warning/amber 4.51:1 (the
// tightest), error/red 5.30:1. These are self-contained pairs (the
// letter is only ever read against its own circle, never the
// surrounding page), so they don't need separate dark-mode values --
// confirmed by rendering both themes (final review, PR #52: inlined
// here since the original measurement notes lived only in a gitignored
// tool-scratch report, .superpowers/sdd/, not anywhere durable).
const PALETTE = [
  { bg: '$badgeNeutralBg', text: '$badgeNeutralText' },
  { bg: '$badgePrimaryBg', text: '$badgePrimaryText' },
  { bg: '$badgeSuccessBg', text: '$badgeSuccessText' },
  { bg: '$badgeWarningBg', text: '$badgeWarningText' },
  { bg: '$badgeErrorBg', text: '$badgeErrorText' },
] as const;

// Shown when the creator/author has no Profile yet at all (the backend
// omits creatorHandle/authorHandle entirely in that case -- see
// backend/internal/http/thread.go and post.go's "OmitsHandleWhenNo
// Profile" tests). Handle is REQUIRED once a Profile exists
// (domain.NewProfile rejects an empty one), so this is a genuine
// no-profile edge case, not the common "hasn't picked a handle yet"
// path -- matches this app's "Nearby"/"what's happening nearby"
// vocabulary rather than a clinical "Unknown".
export const FALLBACK_HANDLE_LABEL = 'Someone nearby';

// The text shown next to the avatar.
export function displayHandle(handle: string | null | undefined): string {
  const trimmed = handle?.trim();
  return trimmed && trimmed.length > 0 ? trimmed : FALLBACK_HANDLE_LABEL;
}

export interface AvatarIdentity {
  initial: string;
  bg: string;
  text: string;
}

// Color is seeded by the handle when there is one, falling back to the
// user id when there isn't (id is always present -- see the no-profile
// case above), so the same person always lands in the same palette
// slot everywhere they appear, and two handle-less posters in the same
// list still read as visually distinct rather than identical gray
// circles. The GLYPH is different: a real handle shows its own first
// letter, but the no-profile case shows "?" rather than a character
// sliced off the raw user id -- a stable color drawn FROM the id is
// harmless (it's one of only five buckets, revealing nothing), but
// printing a literal fragment of the id as if it were an initial would
// misrepresent it as a real name letter, which is exactly the "not a
// raw id" fallback this was asked to avoid.
export function deriveAvatarIdentity(handle: string | null | undefined, id: string): AvatarIdentity {
  const trimmed = handle?.trim();
  const seed = trimmed && trimmed.length > 0 ? trimmed : id;
  const slot = PALETTE[hashToIndex(seed, PALETTE.length)];
  return {
    initial: trimmed && trimmed.length > 0 ? trimmed.charAt(0).toUpperCase() : '?',
    bg: slot.bg,
    text: slot.text,
  };
}
